import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import {
  applyContactScopeFilterViaRelation,
  shouldApplyContactScope,
  assertContactInScope,
  assertOpportunityInScope,
} from '@/lib/chatContactScope';
import {
  formatCrmInteractionDateTime,
  getPipelineStageNames,
  logCrmContactInteraction,
} from '@/lib/crmInteractionLog';
import {
  applyOpportunityDateGte,
  DEFAULT_CRM_DATE_FIELD,
  opportunityNeedsContactInnerJoin,
  type CrmDateField,
} from '@/lib/crmDateFilter';

export type OpportunityStatus = 'open' | 'won' | 'lost';
export type DateFilterPeriod = '30d' | '60d' | '90d' | 'all';
export type { CrmDateField };

export interface Opportunity {
  id: string;
  account_id: string;
  pipeline_id: string;
  stage_id: string;
  contact_id?: string;
  title: string;
  product?: string;
  price: number;
  tags: string[];
  order_index: number;
  contact_tags?: string[];
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  attributed_user_id?: string | null;
  attributed_user_name?: string | null;
  status: OpportunityStatus;
  loss_reason_id?: string | null;
  loss_reason_name?: string | null;
  closed_at?: string | null;
  sale_date?: string | null;
  won_by_user_id?: string | null;
  lost_by_user_id?: string | null;
  notes?: string | null;
  contact_created_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface WonOpportunityData {
  product?: string;
  price?: number;
  sale_date?: string;
  won_by_user_id?: string;
  notes?: string;
}

const getDateFilterValue = (period: DateFilterPeriod): string | null => {
  if (period === 'all') return null;
  const days = parseInt(period);
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
};

export function useOpportunities(
  pipelineId?: string,
  statusFilter: OpportunityStatus | 'all' = 'open',
  dateFilter: DateFilterPeriod = '30d',
  dateField: CrmDateField = DEFAULT_CRM_DATE_FIELD
) {
  const { user } = useAuth();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState<{
    loaded: number;
    total: number;
    isLoadingMore: boolean;
  }>({ loaded: 0, total: 0, isLoadingMore: false });

  const fetchOpportunities = useCallback(async () => {
    if (!user?.account?.id || !supabase) return;

    try {
      setLoading(true);
      setLoadingProgress({ loaded: 0, total: 0, isLoadingMore: false });

      const dateFilterValue = getDateFilterValue(dateFilter);
      const INITIAL_BATCH = dateFilter === 'all' ? 100 : 500;
      const BATCH_SIZE = 250;
      const BATCH_DELAY = 150;

      const needsScope = shouldApplyContactScope(user);
      const needsContactInner =
        needsScope || opportunityNeedsContactInnerJoin(dateField);
      const countSelect = needsContactInner
        ? 'id, contact:account_contacts!inner(attribution_id, created_at)'
        : '*';

      let countQuery = supabase
        .from('account_opportunities')
        .select(countSelect, { count: 'exact', head: true })
        .eq('account_id', user.account.id);

      countQuery = applyContactScopeFilterViaRelation(countQuery, user, 'contact');

      if (pipelineId) {
        countQuery = countQuery.eq('pipeline_id', pipelineId);
      }

      if (statusFilter !== 'all') {
        countQuery = countQuery.eq('status', statusFilter);
      }

      if (dateFilterValue) {
        countQuery = applyOpportunityDateGte(countQuery, dateField, dateFilterValue);
      }

      const { count: totalCount } = await countQuery;
      const total = totalCount || 0;

      // Carregar usuários uma única vez (será reutilizado)
      const { data: usersData } = await supabase
        .from('account_users')
        .select('auth_user_id, name')
        .eq('account_id', user.account.id);

      const usersMap = new Map(usersData?.map(u => [u.auth_user_id, u.name]) || []);

      // Função para transformar dados
      const transformData = (items: any[]) => items.map((item: any) => ({
        ...item,
        contact_tags: item.contact?.tags || [],
        contact_name: item.contact?.name || item.contact?.display_name || item.contact?.push_name_last || 'Contato sem nome',
        contact_phone: item.contact?.phone,
        contact_email: item.contact?.email || '',
        contact_created_at: item.contact?.created_at || null,
        attributed_user_id: item.contact?.attribution_id || null,
        attributed_user_name: item.contact?.attribution_id ? usersMap.get(item.contact.attribution_id) || null : null,
        loss_reason_name: item.loss_reason?.name || null,
        contact: undefined,
        loss_reason: undefined
      }));

      const contactRelation = needsContactInner
        ? 'contact:account_contacts!inner('
        : 'contact:account_contacts(';

      const contactSelectFields = `
            name,
            display_name,
            push_name_last,
            phone,
            email,
            tags,
            attribution_id,
            created_at
      `;

      let initialQuery = supabase
        .from('account_opportunities')
        .select(`
          *,
          ${contactRelation}
            ${contactSelectFields}
          ),
          loss_reason:account_loss_reasons(
            name
          )
        `)
        .eq('account_id', user.account.id)
        .range(0, INITIAL_BATCH - 1)
        .order('created_at', { ascending: false });

      initialQuery = applyContactScopeFilterViaRelation(initialQuery, user, 'contact');

      if (pipelineId) {
        initialQuery = initialQuery.eq('pipeline_id', pipelineId);
      }

      if (statusFilter !== 'all') {
        initialQuery = initialQuery.eq('status', statusFilter);
      }

      if (dateFilterValue) {
        initialQuery = applyOpportunityDateGte(initialQuery, dateField, dateFilterValue);
      }

      const { data: initialData, error: initialError } = await initialQuery;

      if (initialError) throw initialError;

      // Mostrar primeiro lote imediatamente
      const transformedInitial = transformData(initialData || []);
      setOpportunities(transformedInitial);
      setLoading(false);
      setLoadingProgress({
        loaded: transformedInitial.length,
        total,
        isLoadingMore: total > INITIAL_BATCH
      });

      // PASSO 2: Se houver mais dados, carregar em background
      if (total > INITIAL_BATCH) {
        let offset = INITIAL_BATCH;
        let allOpportunities = [...(initialData || [])];

        while (offset < total) {
          // Delay entre requisições para não sobrecarregar
          await new Promise(resolve => setTimeout(resolve, BATCH_DELAY));

          let query = supabase
            .from('account_opportunities')
            .select(`
              *,
              ${contactRelation}
                ${contactSelectFields}
              ),
              loss_reason:account_loss_reasons(
                name
              )
            `)
            .eq('account_id', user.account.id)
            .range(offset, offset + BATCH_SIZE - 1)
            .order('created_at', { ascending: false });

          query = applyContactScopeFilterViaRelation(query, user, 'contact');

          if (pipelineId) {
            query = query.eq('pipeline_id', pipelineId);
          }

          if (statusFilter !== 'all') {
            query = query.eq('status', statusFilter);
          }

          if (dateFilterValue) {
            query = applyOpportunityDateGte(query, dateField, dateFilterValue);
          }

          const { data: batchData, error: batchError } = await query;

          if (batchError) {
            console.error('Erro ao carregar lote:', batchError);
            break;
          }

          if (batchData && batchData.length > 0) {
            allOpportunities = [...allOpportunities, ...batchData];
            const transformed = transformData(allOpportunities);

            setOpportunities(transformed);
            setLoadingProgress({
              loaded: transformed.length,
              total,
              isLoadingMore: transformed.length < total
            });

            offset += batchData.length;

            // Se retornou menos que BATCH_SIZE, não há mais dados
            if (batchData.length < BATCH_SIZE) {
              break;
            }
          } else {
            break;
          }
        }

        // Finalizar carregamento
        setLoadingProgress({
          loaded: allOpportunities.length,
          total: allOpportunities.length,
          isLoadingMore: false
        });
      }
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  }, [
    user?.account?.id,
    user?.auth_user_id,
    user?.view_all_contacts,
    user?.view_unassigned_contacts,
    user?.type,
    pipelineId,
    statusFilter,
    dateFilter,
    dateField,
  ]);

  const createOpportunity = async (opportunity: Omit<Opportunity, 'id' | 'account_id' | 'created_at' | 'updated_at' | 'order_index'>) => {
    if (!user?.account?.id || !supabase) return null;

    try {
      if (opportunity.contact_id) {
        const inScope = await assertContactInScope(
          supabase,
          opportunity.contact_id,
          user.account.id,
          user ?? null,
        );
        if (!inScope) {
          setError('Você não tem permissão para criar oportunidades para este contato');
          return null;
        }
      }

      const { data: inserted, error: createError } = await supabase
        .from('account_opportunities')
        .insert({
          account_id: user.account.id,
          ...opportunity
        })
        .select()
        .single();

      if (createError) throw createError;
      if (!inserted) return null;

      const { data: fullData } = await supabase
        .from('account_opportunities')
        .select(`
          *,
          contact:account_contacts(
            name,
            display_name,
            push_name_last,
            phone,
            email,
            tags,
            attribution_id
          ),
          loss_reason:account_loss_reasons(
            name
          )
        `)
        .eq('id', inserted.id)
        .maybeSingle();

      if (fullData) {
        const { data: usersData } = await supabase
          .from('account_users')
          .select('auth_user_id, name')
          .eq('account_id', user.account.id);

        const usersMap = new Map(usersData?.map(u => [u.auth_user_id, u.name]) || []);

        const transformed: Opportunity = {
          ...fullData,
          contact_tags: fullData.contact?.tags || [],
          contact_name: fullData.contact?.name || fullData.contact?.display_name || fullData.contact?.push_name_last || 'Contato sem nome',
          contact_phone: fullData.contact?.phone,
          attributed_user_id: fullData.contact?.attribution_id || null,
          attributed_user_name: fullData.contact?.attribution_id ? usersMap.get(fullData.contact.attribution_id) || null : null,
          loss_reason_name: fullData.loss_reason?.name || null,
          contact: undefined,
          loss_reason: undefined
        } as Opportunity;

        if (!pipelineId || transformed.pipeline_id === pipelineId) {
          setOpportunities(prev => [transformed, ...prev]);
        }

        return transformed;
      }

      return inserted;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  const checkExistingOpportunity = async (contactId: string, pipelineId: string) => {
    if (!user?.account?.id || !supabase) return null;

    try {
      const { data, error: checkError } = await supabase
        .from('account_opportunities')
        .select('id, title, stage_id')
        .eq('account_id', user.account.id)
        .eq('contact_id', contactId)
        .eq('pipeline_id', pipelineId)
        .maybeSingle();

      if (checkError) throw checkError;
      return data;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  const updateOpportunity = async (id: string, updates: Partial<Opportunity>) => {
    if (!supabase || !user?.account?.id) return false;

    try {
      const inScope = await assertOpportunityInScope(
        supabase,
        id,
        user.account.id,
        user ?? null,
      );
      if (!inScope) {
        setError('Você não tem permissão para alterar esta oportunidade');
        return false;
      }

      const { error: updateError } = await supabase
        .from('account_opportunities')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) throw updateError;
      await fetchOpportunities();
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const deleteOpportunity = async (id: string) => {
    if (!supabase || !user?.account?.id) return false;

    try {
      const inScope = await assertOpportunityInScope(
        supabase,
        id,
        user.account.id,
        user ?? null,
      );
      if (!inScope) {
        setError('Você não tem permissão para remover esta oportunidade');
        return false;
      }

      const { error: deleteError } = await supabase
        .from('account_opportunities')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      await fetchOpportunities();
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const moveOpportunity = async (opportunityId: string, newStageId: string, orderIndex?: number) => {
    if (!supabase || !user?.account?.id) return false;

    try {
      const inScope = await assertOpportunityInScope(
        supabase,
        opportunityId,
        user.account.id,
        user ?? null,
      );
      if (!inScope) {
        setError('Você não tem permissão para mover esta oportunidade');
        return false;
      }

      const opportunity = opportunities.find(o => o.id === opportunityId);
      const previousStageId = opportunity?.stage_id;

      const updateData: Record<string, unknown> = {
        stage_id: newStageId,
        updated_at: new Date().toISOString()
      };
      if (orderIndex !== undefined) {
        updateData.order_index = orderIndex;
      }

      const { error: updateError } = await supabase
        .from('account_opportunities')
        .update(updateData)
        .eq('id', opportunityId);

      if (updateError) throw updateError;

      setOpportunities(prev =>
        prev.map(opp =>
          opp.id === opportunityId
            ? {
                ...opp,
                stage_id: newStageId,
                updated_at: new Date().toISOString(),
                ...(orderIndex !== undefined ? { order_index: orderIndex } : {})
              }
            : opp
        )
      );

      if (opportunity?.contact_id && previousStageId && previousStageId !== newStageId) {
        const stageNames = await getPipelineStageNames([previousStageId, newStageId]);
        const fromStage = stageNames.get(previousStageId) || 'etapa anterior';
        const toStage = stageNames.get(newStageId) || 'nova etapa';
        const userName = user.name || 'Usuário';
        const when = formatCrmInteractionDateTime();
        const oppLabel = opportunity.title || opportunity.contact_name || 'Oportunidade';

        await logCrmContactInteraction({
          accountId: user.account.id,
          contactId: opportunity.contact_id,
          title: 'Movimentação no CRM',
          description: `${userName} moveu a oportunidade "${oppLabel}" de "${fromStage}" para "${toStage}" em ${when}`,
        });
      }

      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const closeOpportunity = async (
    opportunityId: string,
    newStageId: string,
    status: 'won' | 'lost',
    lossReasonId?: string,
    wonData?: WonOpportunityData,
    lostByUserId?: string
  ) => {
    if (!supabase || !user?.account?.id) return false;

    try {
      const inScope = await assertOpportunityInScope(
        supabase,
        opportunityId,
        user.account.id,
        user ?? null,
      );
      if (!inScope) {
        setError('Você não tem permissão para alterar esta oportunidade');
        return false;
      }

      const opportunity = opportunities.find(o => o.id === opportunityId);
      const previousStageId = opportunity?.stage_id;

      const closedAt = new Date().toISOString();
      const updatedAt = new Date().toISOString();
      const updateData: Record<string, unknown> = {
        stage_id: newStageId,
        status,
        closed_at: closedAt,
        updated_at: updatedAt
      };

      // Each outcome clears the other one's fields, otherwise an opportunity that was
      // reopened and closed the other way keeps contradictory data
      if (status === 'lost') {
        if (lossReasonId) updateData.loss_reason_id = lossReasonId;
        updateData.lost_by_user_id = lostByUserId ?? null;
        updateData.won_by_user_id = null;
        updateData.sale_date = null;
      }

      if (status === 'won') {
        updateData.loss_reason_id = null;
        updateData.lost_by_user_id = null;

        if (wonData) {
          if (wonData.product !== undefined) updateData.product = wonData.product;
          if (wonData.price !== undefined) updateData.price = wonData.price;
          if (wonData.sale_date) updateData.sale_date = wonData.sale_date;
          if (wonData.won_by_user_id) updateData.won_by_user_id = wonData.won_by_user_id;
          if (wonData.notes !== undefined) updateData.notes = wonData.notes;
        }
      }

      const { error: updateError } = await supabase
        .from('account_opportunities')
        .update(updateData)
        .eq('id', opportunityId);

      if (updateError) throw updateError;

      setOpportunities(prev =>
        prev.map(opp =>
          opp.id === opportunityId
            ? {
                ...opp,
                stage_id: newStageId,
                status,
                closed_at: closedAt,
                updated_at: updatedAt,
                loss_reason_id: status === 'lost' ? lossReasonId ?? null : null,
                lost_by_user_id: status === 'lost' ? lostByUserId ?? null : null,
                ...(status === 'lost' ? {
                  sale_date: null,
                  won_by_user_id: null
                } : {}),
                ...(status === 'won' && wonData ? {
                  product: wonData.product ?? opp.product,
                  price: wonData.price ?? opp.price,
                  sale_date: wonData.sale_date ?? null,
                  won_by_user_id: wonData.won_by_user_id ?? null,
                  notes: wonData.notes ?? opp.notes
                } : {})
              }
            : opp
        )
      );

      if (opportunity?.contact_id) {
        const stageNames = await getPipelineStageNames([previousStageId, newStageId]);
        const fromStage = previousStageId
          ? stageNames.get(previousStageId) || 'etapa anterior'
          : null;
        const toStage = stageNames.get(newStageId) || 'nova etapa';
        const userName = user.name || 'Usuário';
        const when = formatCrmInteractionDateTime();
        const oppLabel = opportunity.title || opportunity.contact_name || 'Oportunidade';
        const action = status === 'won' ? 'ganha' : 'perdida';
        const stagePart = fromStage
          ? `de "${fromStage}" para "${toStage}"`
          : `na etapa "${toStage}"`;

        await logCrmContactInteraction({
          accountId: user.account.id,
          contactId: opportunity.contact_id,
          title: status === 'won' ? 'Oportunidade ganha' : 'Oportunidade perdida',
          description: `${userName} marcou a oportunidade "${oppLabel}" como ${action} ${stagePart} em ${when}`,
        });
      }

      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const reopenOpportunity = async (opportunityId: string, newStageId: string) => {
    if (!supabase || !user?.account?.id) return false;

    try {
      const inScope = await assertOpportunityInScope(
        supabase,
        opportunityId,
        user.account.id,
        user ?? null,
      );
      if (!inScope) {
        setError('Você não tem permissão para reabrir esta oportunidade');
        return false;
      }

      const opportunity = opportunities.find(o => o.id === opportunityId);
      const previousStageId = opportunity?.stage_id;

      const updatedAt = new Date().toISOString();
      const { error: updateError } = await supabase
        .from('account_opportunities')
        .update({
          stage_id: newStageId,
          status: 'open',
          closed_at: null,
          loss_reason_id: null,
          updated_at: updatedAt
        })
        .eq('id', opportunityId);

      if (updateError) throw updateError;

      setOpportunities(prev =>
        prev.map(opp =>
          opp.id === opportunityId
            ? {
                ...opp,
                stage_id: newStageId,
                status: 'open' as OpportunityStatus,
                closed_at: null,
                loss_reason_id: null,
                updated_at: updatedAt
              }
            : opp
        )
      );

      if (opportunity?.contact_id) {
        const stageNames = await getPipelineStageNames([previousStageId, newStageId]);
        const fromStage = previousStageId
          ? stageNames.get(previousStageId) || 'etapa anterior'
          : null;
        const toStage = stageNames.get(newStageId) || 'nova etapa';
        const userName = user.name || 'Usuário';
        const when = formatCrmInteractionDateTime();
        const oppLabel = opportunity.title || opportunity.contact_name || 'Oportunidade';
        const stagePart = fromStage
          ? `de "${fromStage}" para "${toStage}"`
          : `na etapa "${toStage}"`;

        await logCrmContactInteraction({
          accountId: user.account.id,
          contactId: opportunity.contact_id,
          title: 'Oportunidade reaberta',
          description: `${userName} reabriu a oportunidade "${oppLabel}" ${stagePart} em ${when}`,
        });
      }

      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const reorderOpportunities = async (reorderedItems: { id: string; order_index: number }[]) => {
    if (!supabase) return false;

    try {
      for (const item of reorderedItems) {
        const { error: updateError } = await supabase
          .from('account_opportunities')
          .update({ order_index: item.order_index, updated_at: new Date().toISOString() })
          .eq('id', item.id);

        if (updateError) throw updateError;
      }

      setOpportunities(prev => {
        const updated = [...prev];
        for (const item of reorderedItems) {
          const idx = updated.findIndex(o => o.id === item.id);
          if (idx !== -1) {
            updated[idx] = { ...updated[idx], order_index: item.order_index };
          }
        }
        return updated;
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);

  return {
    opportunities,
    loading,
    error,
    loadingProgress,
    fetchOpportunities,
    createOpportunity,
    checkExistingOpportunity,
    updateOpportunity,
    deleteOpportunity,
    moveOpportunity,
    closeOpportunity,
    reopenOpportunity,
    reorderOpportunities
  };
}
